import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface InvitationData {
  email: string;
  organizationName: string;
  role: string;
  invitedBy: string;
  expiresAt: string;
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const { formatDateOnly } = useDateFormat();
  
  // Form states
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  const [registerForm, setRegisterForm] = useState({
    email: '',
    displayName: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      validateInvitation();
    }
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      // We'll need to create this endpoint in the backend
      const response = await api.validateInvitation(token!);
      setInvitationData(response.data);
      
      // Check if user exists
      const userExists = await checkIfUserExists(response.data.email);
      setIsExistingUser(userExists);
      setActiveStep(userExists ? 1 : 0);
      
      // Pre-fill email in forms
      setLoginForm(prev => ({ ...prev, email: response.data.email }));
      setRegisterForm(prev => ({ ...prev, email: response.data.email }));
      
    } catch (err: any) {
      console.error('Error validating invitation:', err);
      setError(err.response?.data?.message || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const checkIfUserExists = async (email: string): Promise<boolean> => {
    try {
      // We'll need to create this endpoint in the backend
      await api.checkUserExists(email);
      return true;
    } catch (err: any) {
      if (err.response?.status === 404) {
        return false;
      }
      // If there's another error, assume user doesn't exist
      return false;
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      const response = await api.login(loginForm.email, loginForm.password);
      
      // Store the token
      localStorage.setItem('token', response.data.token);
      
      // Accept the invitation
      await api.acceptInvitation(token!, {});
      
      // Redirect to the organization
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    try {
      if (registerForm.password !== registerForm.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);
      
      // Register the user and accept invitation
      const response = await api.acceptInvitation(token!, {
        password: registerForm.password,
        displayName: registerForm.displayName
      });
      
      // Store the token
      localStorage.setItem('token', response.data.token);
      
      // Redirect to the organization
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !invitationData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !invitationData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom>
              Invitation Error
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button 
              variant="contained" 
              onClick={() => navigate('/login')}
              fullWidth
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!invitationData) {
    return null;
  }

  const steps = isExistingUser 
    ? ['Invitation Details', 'Login to Accept']
    : ['Invitation Details', 'Create Account'];

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            You're Invited!
          </Typography>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Invitation Details
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>{invitationData.invitedBy}</strong> has invited you to join{' '}
                <strong>{invitationData.organizationName}</strong> on TimeSentinel.
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {invitationData.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Role:</strong> {invitationData.role}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Expires:</strong> {formatDateOnly(invitationData.expiresAt)}
                </Typography>
              </Box>

              <Button 
                variant="contained" 
                onClick={() => setActiveStep(1)}
                fullWidth
                size="large"
              >
                {isExistingUser ? 'Login to Accept' : 'Create Account'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && isExistingUser && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Login to Accept Invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please log in with your existing account to accept this invitation.
              </Typography>
              
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                margin="normal"
                disabled
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                margin="normal"
                required
              />
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setActiveStep(0)}
                  fullWidth
                >
                  Back
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleLogin}
                  fullWidth
                  disabled={loading || !loginForm.password}
                >
                  {loading ? <CircularProgress size={24} /> : 'Login & Accept'}
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && !isExistingUser && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Create Your Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create a new account to accept this invitation and join the organization.
              </Typography>
              
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                margin="normal"
                disabled
              />
              <TextField
                fullWidth
                label="Display Name"
                value={registerForm.displayName}
                onChange={(e) => setRegisterForm({ ...registerForm, displayName: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                margin="normal"
                required
                error={registerForm.password !== registerForm.confirmPassword && registerForm.confirmPassword !== ''}
                helperText={registerForm.password !== registerForm.confirmPassword && registerForm.confirmPassword !== '' ? 'Passwords do not match' : ''}
              />
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setActiveStep(0)}
                  fullWidth
                >
                  Back
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleRegister}
                  fullWidth
                  disabled={loading || !registerForm.password || !registerForm.displayName || registerForm.password !== registerForm.confirmPassword}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account & Accept'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcceptInvitation;
